import { useDesktopAutoFocus } from '@lib/hooks/sizing/useDesktopAutoFocus';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import css from './WordHintsEditor.module.scss';

/** FE-only cap on a library's transcription glossary, counted in space-separated
 *  words — a single multi-word entry like "Drew Barrymore" counts as 2. */
export const MAX_GLOSSARY_WORDS = 50;

/** Clears up the "words, not entries" confusion next to the counter. */
const GLOSSARY_LIMIT_HELP = `The limit counts words, not entries. A two-word term like "StarZero Agents" uses 2 of your ${MAX_GLOSSARY_WORDS}.`;

/** Count space-separated words in one glossary entry ("Drew Barrymore" → 2). */
export const countWords = (term: string): number => {
  const trimmed = term.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
};

/** Total words across all glossary entries. */
export const countGlossaryWords = (words: string[]): number =>
  words.reduce((sum, term) => sum + countWords(term), 0);

type WordHintsEditorProps = {
  words: string[];
  onChange: (words: string[]) => void;
  disabled?: boolean;
};

/**
 * Add / remove custom word hints (transcription glossary terms) as removable chips.
 * Mirrors the term editor in LibraryRenameModal; kept as its own component so the
 * create-library "Edit words" modal and (later) the rename modal can share it.
 *
 * The glossary is capped at MAX_GLOSSARY_WORDS space-separated words (not entries),
 * so a multi-word term counts for its full word count against the limit.
 */
export function WordHintsEditor({ words, onChange, disabled }: WordHintsEditorProps) {
  const [input, setInput] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autoFocusOnDesktop = useDesktopAutoFocus();

  const usedWords = countGlossaryWords(words);
  const remainingWords = Math.max(0, MAX_GLOSSARY_WORDS - usedWords);
  const atLimit = usedWords >= MAX_GLOSSARY_WORDS;
  const inputWords = countWords(input);
  // Adding the current input would push the total past the cap.
  const wouldExceedLimit = inputWords > 0 && usedWords + inputWords > MAX_GLOSSARY_WORDS;

  const addWord = () => {
    const term = input.trim();
    // !wouldExceedLimit is the single source of truth for the cap; also guards
    // the Enter-key path (which never consults the Add button's disabled state).
    if (term && !words.includes(term) && !wouldExceedLimit) {
      onChange([...words, term]);
      setInput('');
    }
    // Return focus to the input so the user can keep adding — otherwise clicking
    // the "Add" button leaves focus on the button.
    inputRef.current?.focus();
  };

  const removeWord = (term: string) => onChange(words.filter((t) => t !== term));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWord();
    }
  };

  const addDisabled = disabled || !input.trim() || wouldExceedLimit;
  // Only surface a tooltip when the limit is the blocker (empty input just leaves
  // the button quietly disabled).
  const addTooltip = atLimit
    ? `You've reached the ${MAX_GLOSSARY_WORDS}-word limit. Remove a word to add another.`
    : wouldExceedLimit
      ? `Only ${remainingWords} more ${remainingWords === 1 ? 'word' : 'words'} can be added.`
      : '';

  return (
    <div className={css.editor}>
      <div className={css.inputRow}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Add a word"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className={css.input}
          disabled={disabled}
          autoFocus={autoFocusOnDesktop}
        />
        <Tooltip title={addTooltip} placement="top" arrow disableInteractive>
          {/* span wrapper so the tooltip still shows while the button is disabled */}
          <span>
            <ActionButton
              type="button"
              variant="secondary"
              onClick={addWord}
              disabled={addDisabled}
              padding={16}
            >
              Add
            </ActionButton>
          </span>
        </Tooltip>
      </div>
      <div className={css.counterRow}>
        <span className={classNames(css.counter, { [css.counterAtLimit]: atLimit })}>
          {usedWords} / {MAX_GLOSSARY_WORDS} words
        </span>
        <Tooltip
          title={GLOSSARY_LIMIT_HELP}
          placement="top"
          arrow
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          disableTouchListener
        >
          <button
            type="button"
            className={css.infoButton}
            aria-label="How the word limit is counted"
            onClick={() => setHelpOpen((prev) => !prev)}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      </div>
      {words.length > 0 && (
        <div className={css.words}>
          {words.map((term) => (
            <span key={term} className={css.word}>
              {term}
              <button
                type="button"
                onClick={() => removeWord(term)}
                className={css.removeWord}
                disabled={disabled}
                aria-label={`Remove ${term}`}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
